import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    effect,
    inject,
    signal,
    untracked,
    viewChild,
    Pipe,
    PipeTransform,
} from '@angular/core';
import {Store} from '@ngrx/store';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    finalize,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    tap,
    distinctUntilChanged,
} from 'rxjs';
import {selectBuilding} from '../state/selectors';
import {AsyncPipe} from '@angular/common';
import {NgbDropdownModule} from '@ng-bootstrap/ng-bootstrap';
import {
    LocationService,
    LocationWithDetails,
    LocationsConsumer,
} from '@shared/services/location.service';
import {
    ConfirmationDialogService,
    LoadComponent,
    PaginationComponent,
    Sorting,
    SelectableItem,
} from '@brunata-angular/ui';
import {ToastrService} from 'ngx-toastr';
import {
    BuildingServices,
    HiddenValueWhileLoading,
    switchMapWithLoading,
    composeComparators,
    includesIgnoreCase,
    isCurrentConsumer,
    keyComparator,
    numericLocaleComparator,
    openBlobInNewTabOrDownload,
    replaceNamedPlaceholders,
    TranslatePipe,
    TRANSLATE,
    toggleInSet,
} from '@brunata-angular/util';
import {BuildingStateSuccess} from '../state/reducers';
import {takeUntilDestroyed, toObservable, toSignal} from '@angular/core/rxjs-interop';
import {ResidentsListStateService} from './residents-list-state.service';
import {DateTime} from 'luxon';
import {
    ResidentsFilterComponent,
    ResidentsFilterData,
} from './components/residents-filter.component';
import {
    ConsumerInvite,
    ConsumerRowSelection,
    ResidentsTableComponent,
} from './components/residents-table.component';
import {
    consumerRowsInDisplayOrder,
    EnhancedLocationsConsumer,
    EnhancedLocationWithDetails,
    GroupedLocation,
} from './resident-rows';
import {
    ACTIVATION_LETTER_FILE_NAME,
    BatchSignUpDetails,
    ConsumerInviteService,
} from './consumer-invite.service';
import {ConsumerEditService} from './consumer-edit.service';

type ResidentsView = {
    groupedLocations: GroupedLocation[];
    buildingNo: string;
    collectionSize: number;
    showPast: boolean;
    showFuture: boolean;
};

@Pipe({
    name: 'onlineAccess',
    standalone: true,
})
export class OnlineAccessPipe implements PipeTransform {
    transform(value: ResidentsFilterData | null): SelectableItem[] {
        return value?.onlineAccess ?? [];
    }
}

@Component({
    selector: 'app-residents',
    standalone: true,
    imports: [
        AsyncPipe,
        LoadComponent,
        PaginationComponent,
        ResidentsFilterComponent,
        ResidentsTableComponent,
        OnlineAccessPipe,
        TranslatePipe,
        NgbDropdownModule,
    ],
    templateUrl: './residents.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResidentsComponent {
    private readonly filterComponent = viewChild(ResidentsFilterComponent);

    private readonly store = inject(Store);
    private readonly locationService = inject(LocationService);
    private readonly listState = inject(ResidentsListStateService);
    private readonly consumerInviteService = inject(ConsumerInviteService);
    private readonly consumerEditService = inject(ConsumerEditService);
    private readonly confirmationDialog = inject(ConfirmationDialogService);
    private readonly toastr = inject(ToastrService);
    private readonly translate = inject(TRANSLATE);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly isExporting = signal(false);

    // Pagination state
    readonly page = signal(1);
    readonly pageSize = signal(20);

    // Sorting state
    protected readonly sortingSignal = signal<Sorting>(
        {
            column: 'propertyNo',
            direction: 'asc',
        },
        {equal: (a, b) => a.column === b.column && a.direction === b.direction},
    );
    readonly sorting$ = toObservable(this.sortingSignal).pipe(distinctUntilChanged());

    // Filter state
    private readonly initialFilter: ResidentsFilterData = {
        proBraLoc: '',
        name: '',
        email: '',
        phoneNumber: '',
        occupationDate: null,
        occupationEndDate: null,
        onlineAccess: [],
        noEmail: false,
        noPhone: false,
    };
    protected readonly filterSignal = signal<ResidentsFilterData>(this.initialFilter, {
        equal: (a, b) => JSON.stringify(a) === JSON.stringify(b),
    });
    readonly filter$ = toObservable(this.filterSignal).pipe(distinctUntilChanged());

    readonly showPast = signal(false);
    readonly showFuture = signal(false);
    readonly showPast$ = toObservable(this.showPast).pipe(distinctUntilChanged());
    readonly showFuture$ = toObservable(this.showFuture).pipe(distinctUntilChanged());

    // Locations the user expanded one at a time, on top of what the global toggles show.
    readonly expandedLocationIds = signal<ReadonlySet<string>>(new Set());
    readonly expandedLocationIds$ = toObservable(this.expandedLocationIds);

    // Online access options signal
    readonly onlineAccessOptions = signal<SelectableItem[]>([]);

    private readonly initialFilterApplied = signal(false);

    // Snapshot of the filter the parent restored from session state. Bound to the filter child
    // so it can patch its own form on first emission. Stays null when there is nothing to restore.
    protected readonly restoredFilter = signal<ResidentsFilterData | null>(null);

    // The successfully-loaded building number, shared so the view, the "no value" filter
    // availability, and the restore / persist effects all react to one source.
    private readonly buildingNo$: Observable<string> = this.store.select(selectBuilding).pipe(
        filter((state): state is BuildingStateSuccess => state.status === 'success'),
        map(state => state.buildingNo),
        distinctUntilChanged(),
        filter((buildingNo): buildingNo is string => buildingNo !== ''),
        shareReplay(1),
    );

    private readonly buildingNoSignal = toSignal(this.buildingNo$);

    protected readonly hasMyBrunataService = toSignal(
        this.store.select(selectBuilding).pipe(
            filter((state): state is BuildingStateSuccess => state.status === 'success'),
            map(state =>
                state.buildingDetails.buildingServices.includes(BuildingServices.MYBRUNATA),
            ),
            distinctUntilChanged(),
        ),
        {initialValue: false},
    );

    // Locations for the current building, enhanced with the translated online-access label.
    // shareReplay so the view$ and the derived "no value" filter flags share a single fetch.
    private readonly reload$ = new BehaviorSubject<void>(undefined);

    private readonly loadedData$ = this.buildingNo$.pipe(
        tap(() => this.initialFilterApplied.set(false)),
        switchMap(buildingNo =>
            this.reload$.pipe(
                switchMap(() =>
                    this.locationService.getLocations(buildingNo).pipe(
                        map(data => ({
                            ...data,
                            buildingNo,
                            locations: data.locations.map(location => ({
                                ...location,
                                consumers: location.consumers.map(consumer => ({
                                    ...consumer,
                                    onlineAccessLabel: this.translateOnlineAccess(
                                        consumer.onlineAccess,
                                    ),
                                })),
                            })),
                        })),
                    ),
                ),
            ),
        ),
        tap(data => this.onlineAccessOptions.set(this.getUniqueValuesForLists(data.locations))),
        shareReplay(1),
    );

    // Expose the "no value" checkboxes only when at least one current consumer is missing the
    // matching contact detail - derived from the loaded data, not set imperatively per fetch.
    readonly showNoEmailFilter = toSignal(
        this.loadedData$.pipe(
            map(data => this.hasCurrentConsumerMissing(data.locations, 'email')),
            distinctUntilChanged(),
        ),
        {initialValue: false},
    );
    readonly showNoPhoneFilter = toSignal(
        this.loadedData$.pipe(
            map(data => this.hasCurrentConsumerMissing(data.locations, 'phoneNumber')),
            distinctUntilChanged(),
        ),
        {initialValue: false},
    );

    protected readonly inviteInFlight = signal(false);

    private restoredOnce = false;

    readonly view$: Observable<HiddenValueWhileLoading<ResidentsView>> = this.buildingNo$.pipe(
        switchMapWithLoading(buildingNo =>
            this.loadedData$.pipe(
                // loadedData$ replays the previous building's data on a switch; ignore anything
                // that isn't this building until its own fetch resolves.
                filter(data => data.buildingNo === buildingNo),
                switchMap(data =>
                    combineLatest([
                        of(data),
                        this.filter$,
                        this.sorting$,
                        this.showPast$,
                        this.showFuture$,
                        this.expandedLocationIds$,
                    ]).pipe(
                        tap(([, currentFilter]) => {
                            const uniqueValues = this.onlineAccessOptions();
                            // If the filter is currently empty, initialize it with all options
                            if (
                                currentFilter.onlineAccess.length === 0 &&
                                uniqueValues.length > 0 &&
                                !this.initialFilterApplied()
                            ) {
                                this.initialFilterApplied.set(true);
                                this.filterSignal.set({
                                    ...currentFilter,
                                    onlineAccess: uniqueValues,
                                });
                            }
                        }),
                        map(([dataVal, filterData, sorting, showPast, showFuture, expanded]) => {
                            const filteredLocations = this.filterLocations(
                                dataVal.locations,
                                filterData,
                                showPast,
                                showFuture,
                                expanded,
                            );
                            const sortedLocations = this.sortLocations(filteredLocations, sorting);

                            const groupedLocations: GroupedLocation[] = sortedLocations.map(
                                item => ({
                                    location: item.location,
                                    consumers: item.consumers,
                                }),
                            );

                            return {
                                groupedLocations,
                                buildingNo,
                                collectionSize: groupedLocations.length,
                                showPast: showPast === true,
                                showFuture: showFuture === true,
                            };
                        }),
                    ),
                ),
            ),
        ),
        shareReplay(1),
    );

    constructor() {
        // Restore the session snapshot once, the first time the building number is known.
        // untracked() guards against re-reading the persisted snapshot on subsequent saves.
        effect(() => {
            const buildingNo = this.buildingNoSignal();
            if (buildingNo == null || this.restoredOnce) {
                return;
            }
            const snap = untracked(() => this.listState.get(buildingNo));
            if (snap != null) {
                this.filterSignal.set(snap.filter);
                this.sortingSignal.set(snap.sorting);
                this.page.set(snap.page);
                this.pageSize.set(snap.pageSize);
                this.showPast.set(snap.showPast);
                this.showFuture.set(snap.showFuture);
                this.expandedLocationIds.set(snap.expandedLocationIds);
                this.onlineAccessOptions.set(snap.onlineAccessOptions);
                this.initialFilterApplied.set(true);
                this.restoredFilter.set(snap.filter);
            }
            this.restoredOnce = true;
        });

        // Persist on every state change, but only once restoration has settled so a stale save
        // can't clobber an unread snapshot.
        effect(() => {
            const buildingNo = this.buildingNoSignal();
            if (buildingNo == null || !this.restoredOnce) {
                return;
            }
            this.listState.save({
                buildingNo,
                filter: this.filterSignal(),
                sorting: this.sortingSignal(),
                page: this.page(),
                pageSize: this.pageSize(),
                showPast: this.showPast(),
                showFuture: this.showFuture(),
                expandedLocationIds: this.expandedLocationIds(),
                onlineAccessOptions: this.onlineAccessOptions(),
            });
        });
    }

    onFilterChanged(filterData: ResidentsFilterData): void {
        this.filterSignal.set(filterData);
        this.page.set(1);
    }

    protected exportConsumers(view: ResidentsView): void {
        if (this.isExporting()) {
            return;
        }
        this.isExporting.set(true);

        this.locationService
            .downloadLocationsExport(view.buildingNo, this.exportConsumerIds(view))
            .pipe(
                finalize(() => this.isExporting.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                error: () => {
                    // The HTTP error interceptor already shows the generic error toast
                },
            });
    }

    // Every row the table is showing for the current filter, sorting, view toggles and expanded
    // locations. Paging is deliberately not applied - the export covers the whole filtered set,
    // not the visible page.
    // Identified by consumerId: consumerNo is only a per-location sequence and repeats.
    private exportConsumerIds(view: ResidentsView): string[] {
        const today = DateTime.now().startOf('day');
        const expandedLocationIds = this.expandedLocationIds();
        return view.groupedLocations.flatMap(group => {
            const expanded = expandedLocationIds.has(group.location.locationId);
            return consumerRowsInDisplayOrder(
                group,
                view.showPast || expanded,
                view.showFuture || expanded,
                today,
            ).flatMap(row => (row.consumer == null ? [] : [row.consumer.consumerId]));
        });
    }

    toggleShowPast(): void {
        this.showPast.update(v => !v);
    }

    toggleShowFuture(): void {
        this.showFuture.update(v => !v);
    }

    toggleLocationHistory(locationId: string): void {
        this.expandedLocationIds.update(ids => toggleInSet(ids, locationId));
    }

    toggleNoEmail(): void {
        this.filterComponent()?.setNoEmail(!this.filterSignal().noEmail);
    }

    toggleNoPhone(): void {
        this.filterComponent()?.setNoPhone(!this.filterSignal().noPhone);
    }

    protected inviteAllConsumers(): void {
        const buildingNo = this.buildingNoSignal();
        if (buildingNo == null || this.inviteInFlight()) {
            return;
        }

        this.inviteInFlight.set(true);
        this.consumerInviteService
            .getBatchSignUpDetails(buildingNo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: details => void this.confirmAndInvite(buildingNo, details),
                // The error toast is shown by the global ErrorInterceptor.
                error: () => this.inviteInFlight.set(false),
            });
    }

    private async confirmAndInvite(buildingNo: string, details: BatchSignUpDetails): Promise<void> {
        if (details.consumersCount === 0) {
            this.inviteInFlight.set(false);
            this.toastr.info(this.translateResidents('alerts_noConsumersToInvite'));
            return;
        }

        const confirmed = await this.confirmationDialog.confirm(
            this.translate('actions_inviteAllResidents', 'online', 'residents'),
            this.buildInviteMessage(details),
            this.translate('actions_submit', 'online', 'common'),
            this.translate('actions_cancel', 'online', 'common'),
        );
        if (!confirmed) {
            this.inviteInFlight.set(false);
            return;
        }

        this.consumerInviteService
            .inviteAllConsumers(buildingNo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: pdf => this.onInvited(pdf, details),
                error: () => {
                    this.inviteInFlight.set(false);
                    // A failed run can still have committed some invites.
                    this.reload$.next();
                },
            });
    }

    protected inviteConsumer(invite: ConsumerInvite): void {
        const buildingNo = this.buildingNoSignal();
        if (buildingNo == null || this.inviteInFlight()) {
            return;
        }

        this.inviteInFlight.set(true);
        this.consumerInviteService
            .inviteWithConfirmation({buildingNo, ...invite})
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(shouldReload => {
                this.inviteInFlight.set(false);
                if (shouldReload) {
                    this.reload$.next();
                }
            });
    }

    protected editConsumer(selection: ConsumerRowSelection): void {
        const buildingNo = this.buildingNoSignal();
        if (buildingNo == null) {
            return;
        }

        this.consumerEditService
            .editWithModal({
                buildingNo,
                locationId: selection.locationId,
                consumer: selection.consumer,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(saved => {
                if (saved) {
                    this.reload$.next();
                }
            });
    }

    private onInvited(pdf: Blob | null, details: BatchSignUpDetails): void {
        this.inviteInFlight.set(false);
        this.reload$.next();
        if (pdf != null) {
            openBlobInNewTabOrDownload(ACTIVATION_LETTER_FILE_NAME, pdf);
        }
        const key = pdf != null ? 'alerts_inviteAllResidents' : 'alerts_inviteAllResidentsNoPDF';
        const messages = [
            replaceNamedPlaceholders(this.translate(key, 'online', 'residents'), {
                xx: `${details.emailCount}`,
                xxx: `${details.consumersCount}`,
            }),
        ];
        const letterCount = details.consumersCount - details.emailCount;
        if (pdf != null && letterCount > 0) {
            messages.push(
                replaceNamedPlaceholders(
                    this.translateResidents('alerts_inviteAllResidentsLetterCount'),
                    {xx: `${letterCount}`},
                ),
            );
        }
        this.toastr.success(messages.join('. '));
    }

    private buildInviteMessage(details: BatchSignUpDetails): string {
        if (details.emailCount === details.consumersCount) {
            return `<p>${this.translateResidents('information_activateAllResidentModal4')}</p>`;
        }

        const counts = {
            emailCount: `${details.emailCount}`,
            residentCount: `${details.consumersCount}`,
        };
        return [
            `<p>${this.translateResidents('information_activateAllResidentModal1')}</p>`,
            `<h3>${replaceNamedPlaceholders(
                this.translateResidents('information_activateAllResidentModal2'),
                counts,
            )}</h3>`,
            `<p>${replaceNamedPlaceholders(
                this.translateResidents('information_activateAllResidentModal3'),
                counts,
            )}</p>`,
        ].join('');
    }

    private translateResidents(key: string): string {
        return this.translate(key, 'online', 'residents');
    }

    private filterLocations(
        locations: EnhancedLocationWithDetails[],
        filterData: ResidentsFilterData,
        showPast: boolean,
        showFuture: boolean,
        expandedLocationIds: ReadonlySet<string>,
    ): EnhancedLocationWithDetails[] {
        return locations.flatMap(location => {
            const expanded = expandedLocationIds.has(location.location.locationId);
            const proBraLoc = this.getProBraLoc(location);
            const address = `${location.location.street ?? ''} ${
                location.location.stairway ?? ''
            } ${location.location.floor ?? ''} ${location.location.postalCode ?? ''} ${
                location.location.city ?? ''
            }`;
            const matchesProBraLoc =
                includesIgnoreCase(proBraLoc, filterData.proBraLoc ?? '') ||
                includesIgnoreCase(address, filterData.proBraLoc ?? '');
            if (!matchesProBraLoc) {
                return [];
            }

            const matchingConsumers = location.consumers.filter(c =>
                this.matchesConsumerFilter(c, filterData),
            );
            const hasVisibleMatch = matchingConsumers.some(c =>
                this.isVisible(c, showPast || expanded, showFuture || expanded),
            );
            if (!hasVisibleMatch) {
                return [];
            }

            return [{...location, consumers: matchingConsumers}];
        });
    }

    private matchesConsumerFilter(
        consumer: EnhancedLocationsConsumer,
        filterData: ResidentsFilterData,
    ): boolean {
        const matchesName =
            (filterData.name?.length ?? 0) === 0 ||
            includesIgnoreCase(consumer.name ?? '', filterData.name ?? '');
        const matchesEmail =
            filterData.noEmail && this.showNoEmailFilter()
                ? (consumer.email ?? '').trim().length === 0
                : (filterData.email?.length ?? 0) === 0 ||
                  includesIgnoreCase(consumer.email ?? '', filterData.email ?? '');
        const matchesPhoneNumber =
            filterData.noPhone && this.showNoPhoneFilter()
                ? (consumer.phoneNumber ?? '').trim().length === 0
                : (filterData.phoneNumber?.length ?? 0) === 0 ||
                  includesIgnoreCase(consumer.phoneNumber ?? '', filterData.phoneNumber ?? '');
        const matchesOccupationDate =
            filterData.occupationDate == null ||
            this.filterDate(consumer.occupationDate, filterData.occupationDate);
        const matchesOccupationEndDate =
            filterData.occupationEndDate == null ||
            this.filterDate(consumer.occupationEndDate, filterData.occupationEndDate);
        const matchesOnlineAccess =
            filterData.onlineAccess.length === 0 ||
            this.filterLocationValuesFilterList(
                consumer.onlineAccessLabel,
                filterData.onlineAccess,
            );

        return (
            matchesName &&
            matchesEmail &&
            matchesPhoneNumber &&
            matchesOccupationDate &&
            matchesOccupationEndDate &&
            matchesOnlineAccess
        );
    }

    private isVisible(
        consumer: LocationsConsumer,
        showPast: boolean,
        showFuture: boolean,
    ): boolean {
        const today = DateTime.now().startOf('day');
        const isPast = consumer.occupationEndDate != null && consumer.occupationEndDate < today;
        const isFuture = consumer.occupationDate != null && consumer.occupationDate > today;
        if (isPast) {
            return showPast;
        }
        if (isFuture) {
            return showFuture;
        }
        return true;
    }

    private filterDate(date: DateTime | undefined | null, filterValue: DateTime | null): boolean {
        if (!filterValue) {
            return true;
        }
        if (!date) {
            return false;
        }
        return (
            date.day === filterValue.day &&
            date.month === filterValue.month &&
            date.year === filterValue.year
        );
    }

    private filterLocationValuesFilterList(
        value: string,
        filterValue: SelectableItem[] | null,
    ): boolean {
        const valuesToFilter = filterValue?.filter(it => it.selected).map(it => it.name);
        if (!valuesToFilter || valuesToFilter.length === 0) {
            return true;
        }
        return valuesToFilter.includes(value);
    }

    private getUniqueValuesForLists(locations: EnhancedLocationWithDetails[]): SelectableItem[] {
        const valueList: SelectableItem[] = locations.flatMap(location =>
            location.consumers
                .filter(c => this.isCurrent(c))
                .map(c => ({
                    name: c.onlineAccessLabel,
                    selected: false,
                })),
        );

        const uniqBy = <T>(arr: T[], key: (i: T) => string): T[] => [
            ...new Map(arr.map(x => [key(x), x])).values(),
        ];

        return uniqBy(valueList, item => item.name).sort((a, b) => a.name.localeCompare(b.name));
    }

    private hasCurrentConsumerMissing(
        locations: EnhancedLocationWithDetails[],
        key: 'email' | 'phoneNumber',
    ): boolean {
        return locations.some(location =>
            location.consumers.some(
                consumer => this.isCurrent(consumer) && (consumer[key] ?? '').trim().length === 0,
            ),
        );
    }

    private isCurrent(consumer: LocationsConsumer): boolean {
        return isCurrentConsumer(consumer, DateTime.now().startOf('day'));
    }

    private getPrimaryCurrentConsumer(
        location: EnhancedLocationWithDetails,
    ): EnhancedLocationsConsumer | null {
        return location.consumers.find(c => this.isCurrent(c)) ?? null;
    }

    private translateOnlineAccess(val: string | undefined): string {
        if (val === 'ACTIVE') {
            return this.translate('actions_yes', 'online', 'common');
        }
        return this.translate('actions_no', 'online', 'common');
    }

    private sortLocations(
        locations: EnhancedLocationWithDetails[],
        sorting: Sorting,
    ): EnhancedLocationWithDetails[] {
        const sorted = [...locations];
        const direction = sorting.direction;

        if (sorting.column === 'propertyNo') {
            return sorted.sort(
                composeComparators([
                    numericLocaleComparator(
                        location => location.location.propertyNo ?? '',
                        direction,
                    ),
                    numericLocaleComparator(
                        location => location.location.branchNo ?? '',
                        direction,
                    ),
                    numericLocaleComparator(
                        location => location.location.locationNo ?? '',
                        direction,
                    ),
                    keyComparator(
                        location =>
                            this.getPrimaryCurrentConsumer(location)?.occupationDate?.toMillis() ??
                            0,
                        direction,
                    ),
                ]),
            );
        }

        if (sorting.column === 'residentName') {
            return sorted.sort(
                keyComparator(
                    location => this.getPrimaryCurrentConsumer(location)?.name ?? '',
                    direction,
                ),
            );
        }

        if (sorting.column === 'occupationDate') {
            return sorted.sort(
                keyComparator(
                    location =>
                        this.getPrimaryCurrentConsumer(location)?.occupationDate?.toMillis() ?? 0,
                    direction,
                ),
            );
        }

        return sorted;
    }

    onSort(sorting: Sorting): void {
        this.sortingSignal.set(sorting);
    }

    resetFiltersAndSorting(): void {
        this.page.set(1);
        this.sortingSignal.set({
            column: 'propertyNo',
            direction: 'asc',
        });
        this.filterComponent()?.reset();
    }

    setPage(page: number): void {
        this.page.set(page);
    }

    setPageSize(pageSize: number): void {
        this.pageSize.set(pageSize);
    }

    private getProBraLoc(location: LocationWithDetails): string {
        const loc = location.location;
        const propertyNo = loc.propertyNo ?? '';
        const branchNo = loc.branchNo ?? '';
        const locationNo = loc.locationNo ?? '';
        return `${propertyNo}-${branchNo}-${locationNo}`;
    }
}
